import { createCustomerPortalSession } from "@/api/privateApi";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { cn } from "@/lib/utils";
import { Subscription, userSubscriptionSelector } from "@/redux/user/userSlice";
import { t, useTranslation } from "@repo/shared/i18n";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { ResetEvolutionsDefaultPopup } from "../../../pages/EvolutionsHub/Popups/ResetEvolutionsDefaultPopup";
import { showModal } from "../../../redux/message/messageSlice";
import { AppDispatch } from "../../../redux/store";
import { BaseModal } from "./BaseModal";
import { LoginModal } from "./SpecificModals/LoginModal";
import { SaveSquadSection } from "./SpecificModals/SaveSquadSection";
import ShareSquadSection from "./SpecificModals/ShareSquadSection";
import { WelcomeModal } from "./SpecificModals/WelcomeModal";

export type ModalType =
  | "premium-feature"
  | "sign-in-to-subscribe"
  | "already-subscriber"
  | "login"
  | "login-required"
  | "share-squad"
  | "save-squad"
  | "ios-import-popup"
  | "reset-evolutions-default"
  | "welcome";

/**
 * Mixpanel `upgrade` payload. The premium-feature popup fires the `upgrade`
 * event on the "Buy Now" click (purchase intent), NOT when the popup is shown.
 * The popup itself has no knowledge of the section/subsection that opened it, so
 * the dispatcher supplies it here.
 */
export type UpgradeTracking = {
  section: string;
  subsection: string;
  context: string;
};

type ModalBase = {
  premiumFeatureHook?: premiumFeatureHooks;
  freeFeatureLoggedInRequiredHook?: freeFeatureLoggedInRequiredHooks;
  data?: any; // Generic data field for modal-specific data
};

export type Modal =
  | (ModalBase & {
      modalType: "premium-feature";
      // Required: every premium-feature modal must supply upgrade tracking so
      // the `upgrade` event fires with section/subsection/context on Buy Now.
      // Making this non-optional makes a missing payload a compile error.
      upgradeTracking: UpgradeTracking;
    })
  | (ModalBase & {
      modalType: Exclude<ModalType, "premium-feature">;
      upgradeTracking?: UpgradeTracking;
    });

// Potentially modify URL in order to hide ads

export type freeFeatureLoggedInRequiredHooks =
  | "upvote/downvote-sbc"
  | "hide-sbc"
  | "favorite-sbc"
  | "complete-sbc"
  | "player-inclusion"
  | "player-exclusion"
  | "favorite-tactic"
  | "unlock-chains"
  | "change-evo-state"
  | "save-evo"
  | "reset-web-app-state"
  | "see-my-evos"
  | "see-own-players"
  | "see-active-squad"
  | "import-club"
  | "solve-sbc-limit";

type premiumFeatureHooks =
  | "build-squad-with-club"
  | "include-loan-players"
  | "solve-sbc-with-club"
  | "see-own-players"
  | "see-own-evo-players"
  | "see-active-squad"
  | "import-club";

// Key per hook, resolved with t() when the modal is built (i18n inits after import).
const freeFeatureLoggedInRequiredHooks = {
  "upvote/downvote-sbc": { description: "loginRequired.upvoteDownvoteSbc" },
  "hide-sbc": { description: "loginRequired.hideSbc" },
  "favorite-sbc": { description: "loginRequired.favoriteSbc" },
  "complete-sbc": { description: "loginRequired.completeSbc" },
  "player-inclusion": { description: "loginRequired.playerInclusion" },
  "player-exclusion": { description: "loginRequired.playerExclusion" },
  "favorite-tactic": { description: "loginRequired.favoriteTactic" },
  "unlock-chains": { description: "loginRequired.unlockChains" },
  "change-evo-state": { description: "loginRequired.changeEvoState" },
  "reset-web-app-state": { description: "loginRequired.resetWebAppState" },
  "save-evo": { description: "loginRequired.saveEvo" },
  "see-my-evos": { description: "loginRequired.seeMyEvos" },
  "see-own-players": { description: "loginRequired.seeOwnPlayers" },
  "see-active-squad": { description: "loginRequired.seeActiveSquad" },
  "import-club": { description: "loginRequired.importClub" },
  "solve-sbc-limit": { description: "loginRequired.solveSbcLimit" },
} as const satisfies Record<
  freeFeatureLoggedInRequiredHooks,
  { description: string }
>;

const getPremiumFeatureHooks = (
  premiumFeatureHook: premiumFeatureHooks,
  currentSubscription?: Subscription,
): {
  description: string;
  title?: string;
  learnMoreLink?: string;
} => {
  switch (premiumFeatureHook) {
    case "build-squad-with-club":
      return {
        description:
          currentSubscription === "gold"
            ? t("premiumFeature.buildSquadWithClub.upgrade")
            : t("premiumFeature.buildSquadWithClub.join"),
      };
    case "solve-sbc-with-club":
      return {
        description: t("premiumFeature.solveSbcWithClub"),
        learnMoreLink: "/get-sbc-solver",
      };
    case "see-own-players":
      return {
        description: t("premiumFeature.seeOwnPlayers"),
        learnMoreLink: "/get-my-players",
      };
    case "see-own-evo-players":
      return {
        description: t("premiumFeature.seeOwnEvoPlayers"),
        learnMoreLink: "/get-my-players",
      };
    case "see-active-squad":
      return {
        description: t("premiumFeature.seeActiveSquad"),
        learnMoreLink: "/get-my-players",
      };
    case "import-club":
      return {
        title: t("premiumFeature.title"),
        description: t("premiumFeature.importClub"),
      };
    case "include-loan-players":
      return {
        description:
          currentSubscription === "gold"
            ? t("premiumFeature.includeLoanPlayers.upgrade")
            : t("premiumFeature.includeLoanPlayers.join"),
      };
    default:
      return {
        description: t("premiumFeature.default"),
      };
  }
};

export const ModalController = ({
  modalType,
  premiumFeatureHook,
  freeFeatureLoggedInRequiredHook,
  upgradeTracking,
  data,
}: Modal) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [clickedPositiveAction, setClickedPositiveAction] = useState(false);
  const userSubscription = useSelector(userSubscriptionSelector);
  const { t: tc } = useTranslation(["common", "squadBuilder"]);

  const closeModal = () => {
    dispatch(showModal());
  };

  const MODAL_CONFIGS = {
    "premium-feature": {
      title:
        getPremiumFeatureHooks(premiumFeatureHook, userSubscription)?.title ??
        tc("common:premiumFeature.title"),
      description: premiumFeatureHook
        ? getPremiumFeatureHooks(premiumFeatureHook, userSubscription)
            ?.description
        : tc("common:premiumFeature.default"),
      type: "action",
      onClose: closeModal,
      actions: [
        { label: tc("common:close"), onClick: closeModal, variant: "negative" },
        {
          label: tc("common:buyNow"),
          onClick: () => {
            // Fire the `upgrade` event here — on the actual "Buy Now" click
            // (purchase intent) — rather than when the popup is shown. The
            // dispatcher supplies section/subsection/context via upgradeTracking.
            if (upgradeTracking) {
              mixpanelTrack("upgrade", {
                ...upgradeTracking,
                action_type: "lifecycle",
              });
            }
            if (userSubscription === "gold") {
              createCustomerPortalSession();
            } else {
              navigate("/subscription");
              closeModal();
            }
          },
          variant: "positive",
        },
        premiumFeatureHook &&
          getPremiumFeatureHooks(premiumFeatureHook)?.learnMoreLink && {
            onClick: () => {
              navigate(
                getPremiumFeatureHooks(premiumFeatureHook)?.learnMoreLink,
                {
                  state: { from: window.location.pathname },
                },
              );
              closeModal();
            },
            variant: "learn-more",
          },
      ],
    },
    "already-subscriber": {
      title: tc("common:dialog.alreadySubscriber.title"),
      content: tc("common:dialog.alreadySubscriber.body"),
      type: "action",
      actions: [
        { label: tc("common:close"), onClick: closeModal, variant: "negative" },
        {
          label: tc("common:dialog.alreadySubscriber.goToMainPage"),
          onClick: () => {
            navigate("/");
            closeModal();
          },
          variant: "positive",
        },
      ],
      onClose: closeModal,
    },
    "share-squad": {
      title: tc("squadBuilder:shareSquad.title"),
      type: "action",
      onClose: closeModal,
      children: <ShareSquadSection squadData={data} />,
      showCloseButton: true,
    },
    "save-squad": {
      title: tc("squadBuilder:saveSquad.title"),
      type: "action",
      actions: !clickedPositiveAction
        ? [
            {
              label: tc("common:close"),
              onClick: closeModal,
              variant: "negative",
            },
            {
              label: tc("common:save"),
              onClick: () => setClickedPositiveAction(true),
              variant: "positive",
            },
          ]
        : [
            {
              label: tc("common:close"),
              onClick: closeModal,
              variant: "negative",
            },
          ],
      // titleIcon: saveSquadIcon,
      onClose: closeModal,
      children: (
        <div className="px-4">
          {clickedPositiveAction && (
            <p className="text-sm text-dark-secondary mt-4 -mb-6">
              {tc("squadBuilder:saveSquad.loginToSave")}
            </p>
          )}
          <SaveSquadSection
            clickedSave={clickedPositiveAction}
            onClose={closeModal}
          />
        </div>
      ),
    },
  };

  if (!modalType) return null;

  if (modalType === "welcome") {
    return <WelcomeModal welcomeBack={data?.showWelcomeBack ?? false} />;
  }

  // Login modals are self-contained and manage their own title/description
  if (
    modalType === "login" ||
    modalType === "login-required" ||
    modalType === "sign-in-to-subscribe"
  ) {
    const sessionExpired = modalType === "login" && data?.reason === "expired";
    const loginTitle =
      modalType === "sign-in-to-subscribe"
        ? tc("common:dialog.login.subscribeTitle")
        : sessionExpired
          ? tc("common:dialog.login.sessionExpiredTitle")
          : modalType === "login"
            ? tc("common:dialog.login.loginTitle")
            : tc("common:dialog.login.createAccountTitle");
    const requiredKey =
      freeFeatureLoggedInRequiredHooks[freeFeatureLoggedInRequiredHook]
        ?.description;
    const loginDescription =
      modalType === "sign-in-to-subscribe"
        ? tc("common:dialog.login.subscribeBody")
        : sessionExpired
          ? tc("common:dialog.login.sessionExpiredBody")
          : modalType === "login"
            ? tc("common:dialog.login.loginBody")
            : requiredKey && t(requiredKey);

    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black/60 z-100 flex justify-center items-center">
        <LoginModal
          onClose={closeModal}
          title={loginTitle}
          description={loginDescription}
        />
      </div>
    );
  }

  if (modalType === "reset-evolutions-default") {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black/60 z-100 flex justify-center items-center">
        <ResetEvolutionsDefaultPopup data={data} onClose={closeModal} />
      </div>
    );
  }

  /*
    z-100 is the modal layer, the same one BaseModalV2, NowImporting and the
    nav drawer sit in. These used to be z-50 — the panel layer — so any modal
    raised from a bottom sheet, sidebar or other z-50 fixed panel lost to it on
    DOM order and appeared to do nothing when clicked. Popovers (z-110) stay
    above, so one opened inside a modal is still visible.
  */
  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black/60 z-100 flex justify-center items-center",
        MODAL_CONFIGS[modalType]?.notCentered
          ? "items-start pt-8 mobile:pt-16 large-desktop:pt-24"
          : "items-center top-0",
      )}
    >
      <BaseModal
        title={MODAL_CONFIGS[modalType]?.title}
        titleIcon={MODAL_CONFIGS[modalType]?.titleIcon}
        description={MODAL_CONFIGS[modalType]?.description}
        type={MODAL_CONFIGS[modalType]?.type}
        actions={MODAL_CONFIGS[modalType]?.actions}
        className={MODAL_CONFIGS[modalType]?.className}
        onClose={MODAL_CONFIGS[modalType]?.onClose}
        showCloseButton={MODAL_CONFIGS[modalType]?.showCloseButton}
      >
        {MODAL_CONFIGS[modalType]?.children}
      </BaseModal>
    </div>
  );
};
